export const SCROLL_END_HOLD_SECONDS = 10;
export const SCROLL_END_FADE_SECONDS = 1;

export const getCenteredLastBlockScrollTop = (el) => {
  if (!el) return 0;
  const wrappers = el.querySelectorAll(".blockWrapper");
  const last = wrappers[wrappers.length - 1];
  const naturalMax = Math.max(0, el.scrollHeight - el.clientHeight);
  if (!last) return naturalMax;

  const frame = el.getBoundingClientRect();
  const box = last.getBoundingClientRect();
  // The final wrapper is counter-translated during the hold. Remove that
  // display-only offset from the measurement so the center stop remains
  // stable instead of chasing the transformed block on every animation frame.
  const heldOffset =
    parseFloat(last.style.getPropertyValue("--preview-end-hold-offset")) || 0;
  const zoomWrapper = last.closest("#preview-zoom-wrapper");
  const displayZoom =
    parseFloat(zoomWrapper?.style?.zoom) ||
    parseFloat(window.getComputedStyle(zoomWrapper || last).zoom) ||
    1;
  const relativeTop =
    el.scrollTop + box.top - frame.top - heldOffset * displayZoom;
  return Math.min(
    naturalMax,
    Math.max(0, relativeTop + box.height / 2 - el.clientHeight / 2),
  );
};

export const getScrollTimeline = ({ el, rate, endHoldEnabled }) => {
  const naturalDistance = Math.max(
    0,
    (el?.scrollHeight || 0) - (el?.clientHeight || 0),
  );
  const motionDistance = endHoldEnabled
    ? getCenteredLastBlockScrollTop(el)
    : naturalDistance;
  const safeRate = Math.max(0.0001, Number(rate) || 0);
  const motionDuration = motionDistance / safeRate;
  const duration =
    motionDuration + (endHoldEnabled ? SCROLL_END_HOLD_SECONDS : 0);

  return { naturalDistance, motionDistance, motionDuration, duration };
};

export const getScrollTimelineState = ({
  elapsed,
  naturalDistance,
  motionDistance,
  motionDuration,
  duration,
  rate,
  endHoldEnabled,
}) => {
  const safeElapsed = Math.min(Math.max(0, elapsed), duration);
  const maxScroll = Number.isFinite(naturalDistance)
    ? naturalDistance
    : motionDistance;
  const scrollTop = Math.min(maxScroll, safeElapsed * rate);
  const isHolding = endHoldEnabled && safeElapsed >= motionDuration;
  const holdOffset = isHolding
    ? Math.max(0, scrollTop - motionDistance)
    : 0;
  let opacity = 1;

  if (endHoldEnabled) {
    const fadeStart = duration - SCROLL_END_FADE_SECONDS;
    if (safeElapsed > fadeStart) {
      opacity = Math.max(
        0,
        1 - (safeElapsed - fadeStart) / SCROLL_END_FADE_SECONDS,
      );
    }
  }

  return {
    elapsed: safeElapsed,
    scrollTop,
    holdOffset,
    opacity,
    progress: duration > 0 ? safeElapsed / duration : 0,
    isHolding,
  };
};

export const getEndHoldVisualState = ({
  state,
  actualScrollTop,
  motionDistance,
  endHoldEnabled,
}) => {
  if (!endHoldEnabled) return state;

  // Browsers may quantize scrollTop to whole CSS pixels. Counteract only that
  // rounding on the final block before the hold, then reuse the same transform
  // to pin it once the rest of the credits continue past center.
  const visualScrollTop = state.isHolding
    ? motionDistance
    : state.scrollTop;

  return {
    ...state,
    isEndHoldTarget: true,
    holdOffset: Number(actualScrollTop) - visualScrollTop,
  };
};

// RenderX2 truncates scroll_pixel_per_frame to a whole-pixel step, so a 3.2
// speed actually moves 3px per frame. Frame counts must use that step.
export const getRenderXStep = (scrollPixelPerFrame) =>
  Math.max(1, Math.floor(Number(scrollPixelPerFrame) || 0));

export const getRenderEndHoldTiming = ({
  images,
  videoHeight,
  scrollPixelPerFrame,
  fpsNumerator,
  fpsDenominator,
}) => {
  const creditImages = Array.isArray(images) ? images : [];
  const last = creditImages[creditImages.length - 1];
  const pxPerFrame = Number(scrollPixelPerFrame);
  const fpsNum = Number(fpsNumerator);
  const fpsDen = Number(fpsDenominator);
  const frameHeight = Number(videoHeight);
  const lastHeight = Number(last?.height) || 0;
  if (
    !last ||
    !pxPerFrame ||
    !fpsNum ||
    !fpsDen ||
    !(frameHeight > 0) ||
    !(lastHeight > 0)
  ) {
    return null;
  }
  const beforeLast = creditImages
    .slice(0, -1)
    .reduce((sum, image) => sum + (Number(image.height) || 0), 0);
  const step = getRenderXStep(pxPerFrame);

  // RenderX2 advances one PNG at a time. When a frame step crosses an image
  // boundary, the overshoot is discarded instead of being carried into the
  // next PNG. Each image therefore occupies ceil(height / step) render frames,
  // rather than all PNGs behaving like one continuous strip. Tiny remainders
  // accumulate across long rolls and can otherwise put the separated final
  // block almost a full screen ahead of the base video.
  const imageFrameSpan = (image) =>
    Math.ceil(Math.max(0, Number(image?.height) || 0) / step);
  const leadingBlankFrames = Math.ceil(frameHeight / step);
  const beforeLastFrames = creditImages
    .slice(0, -1)
    .reduce((sum, image) => sum + imageFrameSpan(image), 0);
  const lastBlockStartFrame = leadingBlankFrames + beforeLastFrames;
  const centeredOffsetFrames = Math.round(
    (lastHeight - frameHeight) / (2 * step),
  );
  const stopFrame = Math.max(0, lastBlockStartFrame + centeredOffsetFrames);

  return {
    stopSeconds: stopFrame * (fpsNum / fpsDen),
    holdSeconds: SCROLL_END_HOLD_SECONDS,
    fadeSeconds: SCROLL_END_FADE_SECONDS,
    lastBlockTopPixels: beforeLast,
    lastBlockHeightPixels: lastHeight,
    lastBlockStartFrame,
  };
};
